import { Link, useParams } from 'react-router-dom';
import { getServiceOfferingById } from 'wasp/client/operations';

export default function ServiceOfferingDetailPage() {
  const { id } = useParams();
  const { data: offering, isLoading, error } = getServiceOfferingById({ id: id! }) as any;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading service...</p>
        </div>
      </div>
    );
  }

  if (error || !offering) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 text-lg mb-4">Service offering not found</p>
          <Link to="/services/offerings" className="text-blue-600 hover:text-blue-800">
            Back to Offerings
          </Link>
        </div>
      </div>
    );
  }

  const formatSchedule = () => {
    if (!offering.scheduleType) return null;

    const parts: string[] = [];
    parts.push(offering.scheduleType.charAt(0).toUpperCase() + offering.scheduleType.slice(1));

    if (offering.daysOfWeek && offering.daysOfWeek.length > 0) {
      const days: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayNames: string = offering.daysOfWeek.map((d: number) => days[d]).join(', ');
      parts.push(`on ${dayNames}`);
    }

    if (offering.timeOfDay) {
      parts.push(`at ${offering.timeOfDay}`);
    }

    return parts.join(' ');
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm">
          <Link to="/discover-services" className="text-blue-600 hover:text-blue-800">
            Services
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/discover-services/offerings" className="text-blue-600 hover:text-blue-800">
            Available Services
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600 dark:text-gray-400">
            {offering.customName || offering.serviceDefinition.name}
          </span>
        </nav>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {offering.customName || offering.serviceDefinition.name}
              </h1>

              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 font-medium">
                  {offering.serviceDefinition.serviceType.label}
                </span>
                {offering.requiresRegistration && (
                  <span className="px-4 py-2 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 font-medium">
                    Registration Required
                  </span>
                )}
                {offering.visibility && (
                  <span className="px-4 py-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 font-medium capitalize">
                    {offering.visibility.toLowerCase()}
                  </span>
                )}
              </div>

              {/* Description */}
              {offering.customDescription ? (
                <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
                  {offering.customDescription}
                </p>
              ) : (
                <p className="text-gray-700 dark:text-gray-300 text-lg mb-6">
                  {offering.serviceDefinition.description}
                </p>
              )}

              {/* Service Details */}
              <div className="grid gap-6 sm:grid-cols-2 pt-6 border-t border-gray-200 dark:border-gray-700">
                {offering.scheduleType && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Schedule</h3>
                    <p className="text-gray-900 dark:text-white">{formatSchedule()}</p>
                  </div>
                )}

                {offering.startDate && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Start Date</h3>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(offering.startDate).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {offering.endDate && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">End Date</h3>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(offering.endDate).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {offering.hasCapacity && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Capacity</h3>
                    <p className="text-gray-900 dark:text-white">
                      {offering.currentAttendees} / {offering.maxCapacity} spots filled
                    </p>
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(offering.currentAttendees / offering.maxCapacity) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Location</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Name</h3>
                  <p className="text-gray-900 dark:text-white">{offering.location.name}</p>
                </div>
                {offering.location.address && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Address</h3>
                    <p className="text-gray-900 dark:text-white">
                      {offering.location.address}
                      {offering.location.city && `, ${offering.location.city}`}
                      {offering.location.state && `, ${offering.location.state}`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Organization */}
            {offering.organization && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Organized By</h2>
                <p className="text-gray-900 dark:text-white text-lg">{offering.organization.name}</p>
                {offering.organization.description && (
                  <p className="text-gray-600 dark:text-gray-400 mt-2">{offering.organization.description}</p>
                )}
              </div>
            )}

            {/* Upcoming Events */}
            {offering.events && offering.events.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Upcoming Events</h2>
                <div className="space-y-3">
                  {offering.events.map((event: any) => (
                    <div key={event.id} className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{event.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(event.startTime).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 sticky top-8 space-y-6">
              {/* Contact Info */}
              {(offering.contactPerson || offering.contactPhone || offering.contactEmail) && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Contact Information</h3>
                  <div className="space-y-3 text-sm">
                    {offering.contactPerson && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 mb-1">Contact Person</p>
                        <p className="text-gray-900 dark:text-white">{offering.contactPerson}</p>
                      </div>
                    )}
                    {offering.contactPhone && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 mb-1">Phone</p>
                        <a href={`tel:${offering.contactPhone}`} className="text-blue-600 hover:text-blue-800">
                          {offering.contactPhone}
                        </a>
                      </div>
                    )}
                    {offering.contactEmail && (
                      <div>
                        <p className="text-gray-600 dark:text-gray-400 mb-1">Email</p>
                        <a href={`mailto:${offering.contactEmail}`} className="text-blue-600 hover:text-blue-800">
                          {offering.contactEmail}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Registration */}
              {offering.requiresRegistration && (
                <div>
                  {offering.registrationUrl ? (
                    <a
                      href={offering.registrationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-center rounded-lg font-medium transition"
                    >
                      Register Now
                    </a>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      Contact the organization to register
                    </p>
                  )}
                </div>
              )}

              {/* Service Definition Link */}
              <div>
                <Link
                  to={`/discover-services/definitions/${offering.serviceDefinition.slug}` as any}
                  className="block w-full px-6 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-center rounded-lg font-medium transition"
                >
                  View Service Type Details
                </Link>
              </div>

              {/* Related Topics */}
              {offering.serviceDefinition.topicServices && offering.serviceDefinition.topicServices.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Related Topics</h3>
                  <div className="flex flex-wrap gap-2">
                    {offering.serviceDefinition.topicServices.map((ts: any) => (
                      <Link
                        key={ts.topic.id}
                        to={`/topics/${ts.topic.slug}` as any}
                        className="text-xs px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 hover:bg-purple-200 dark:hover:bg-purple-800 transition"
                      >
                        {ts.topic.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
}



